// Relances section embedded inside the Voice editor (EPIC-12).
//
// Top-level surface that wires `useRelancesConfig` + the per-type CRUD hooks
// to the visible `RelanceTypeCard` + `CustomTypeEditor` rows. Auto-save on
// every change — there's no explicit "Save" button on this surface (the
// parent voice page has its own bottom action bar).

import { useT } from '@barel/i18n';
import {
  useCreateCustomRelanceType,
  useDeleteCustomRelanceType,
  useRelancesConfig,
  useUpdateCustomRelanceType,
  useUpdateRelancesConfig,
  type RelanceCustomTypeRow,
  type RelancesConfigPatch,
  type RelancesConfigRow,
} from '@barel/api-client/react';
import { Skeleton, Switch, toast } from '@barel/ui';

import {
  AddCustomTypeButton,
  RelanceTypeCard,
  type BuiltInTypeConfig,
  type BuiltInTypeKind,
} from './RelanceTypeCard';
import { CustomTypeEditor } from './CustomTypeEditor';

export interface RelancesSectionProps {
  linkedinAccountId: string;
}

export function RelancesSection({ linkedinAccountId }: RelancesSectionProps) {
  const t = useT('relances');
  const tVoice = useT('voice');
  const query = useRelancesConfig(linkedinAccountId);
  const updateConfig = useUpdateRelancesConfig(linkedinAccountId);
  const createCustom = useCreateCustomRelanceType(linkedinAccountId);
  const updateCustom = useUpdateCustomRelanceType(linkedinAccountId);
  const deleteCustom = useDeleteCustomRelanceType(linkedinAccountId);

  const isLoading = query.isLoading;
  const config = query.data?.config;
  const customTypes = query.data?.customTypes ?? [];

  const flushConfig = async (patch: RelancesConfigPatch): Promise<void> => {
    try {
      await updateConfig.mutateAsync(patch);
    } catch {
      toast.error({ title: t('saveError') });
    }
  };

  const updateBuiltIn = async (kind: BuiltInTypeKind, next: BuiltInTypeConfig) => {
    if (kind === 'catchup') {
      await flushConfig({
        catchupEnabled: next.enabled,
        catchupDelayDays: next.delayDays,
        catchupMaxAttempts: next.maxAttempts,
        catchupHint: next.hint,
      });
    } else if (kind === 'project') {
      await flushConfig({
        projectEnabled: next.enabled,
        projectDelayDays: next.delayDays,
        projectMaxAttempts: next.maxAttempts,
        projectHint: next.hint,
      });
    } else {
      await flushConfig({
        lastShotEnabled: next.enabled,
        lastShotDelayDays: next.delayDays,
        lastShotMaxAttempts: next.maxAttempts,
        lastShotHint: next.hint,
      });
    }
  };

  const builtInConfig = (kind: BuiltInTypeKind, c: RelancesConfigRow): BuiltInTypeConfig => {
    if (kind === 'catchup') {
      return {
        enabled: c.catchupEnabled,
        delayDays: c.catchupDelayDays,
        maxAttempts: (c.catchupMaxAttempts === 2 ? 2 : 1) as 1 | 2,
        hint: c.catchupHint ?? '',
      };
    }
    if (kind === 'project') {
      return {
        enabled: c.projectEnabled,
        delayDays: c.projectDelayDays,
        maxAttempts: (c.projectMaxAttempts === 2 ? 2 : 1) as 1 | 2,
        hint: c.projectHint ?? '',
      };
    }
    return {
      enabled: c.lastShotEnabled,
      delayDays: c.lastShotDelayDays,
      maxAttempts: (c.lastShotMaxAttempts === 2 ? 2 : 1) as 1 | 2,
      hint: c.lastShotHint ?? '',
    };
  };

  const handleCreateCustom = async (): Promise<void> => {
    try {
      await createCustom.mutateAsync({ label: '', whenText: '', hint: '' });
      toast.success({ title: t('custom.createSuccess') });
    } catch {
      toast.error({ title: t('saveError') });
    }
  };

  const handlePatchCustom = async (
    row: RelanceCustomTypeRow,
    patch: Partial<
      Pick<
        RelanceCustomTypeRow,
        'label' | 'whenText' | 'hint' | 'delayDays' | 'maxAttempts' | 'enabled'
      >
    >,
  ): Promise<void> => {
    try {
      await updateCustom.mutateAsync({ id: row.id, patch });
    } catch {
      toast.error({ title: t('saveError') });
    }
  };

  const handleDeleteCustom = async (row: RelanceCustomTypeRow): Promise<void> => {
    try {
      await deleteCustom.mutateAsync({ id: row.id });
      toast.success({ title: t('custom.deleteSuccess') });
    } catch {
      toast.error({ title: t('saveError') });
    }
  };

  if (isLoading || !config) {
    return (
      <div className="flex flex-col gap-3" data-testid="relances-loading">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-component="RelancesSection">
      <div
        data-testid="relances-master-switch"
        className={
          'rounded-xl border px-4 py-3 transition-colors duration-fast ease-out ' +
          (config.enabled ? 'border-accent-border bg-accent-dim' : 'border-default bg-bg-surface')
        }
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-primary">{t('masterSwitch.title')}</div>
            <p className="mt-0.5 text-xs text-secondary leading-relaxed">
              {t('masterSwitch.body')}
            </p>
          </div>
          <Switch
            aria-label={t('masterSwitch.title')}
            size="md"
            checked={config.enabled}
            onCheckedChange={(next: boolean) => {
              void flushConfig({ enabled: next });
            }}
            data-testid="relances-master-switch-input"
          />
        </div>
      </div>

      {config.enabled ? (
        <div className="flex flex-col gap-3" data-testid="relances-types">
          {(['catchup', 'project', 'lastShot'] as const).map((kind) => (
            <RelanceTypeCard
              key={kind}
              kind={kind}
              config={builtInConfig(kind, config)}
              onChange={(next) => {
                void updateBuiltIn(kind, next);
              }}
              disabled={updateConfig.isPending}
            />
          ))}
          {customTypes.map((row) => (
            <CustomTypeEditor
              key={row.id}
              type={row}
              onPatch={(patch) => handlePatchCustom(row, patch)}
              onDelete={() => handleDeleteCustom(row)}
              busy={updateCustom.isPending || deleteCustom.isPending}
            />
          ))}
          <AddCustomTypeButton
            onClick={() => {
              void handleCreateCustom();
            }}
            disabled={createCustom.isPending}
          />
        </div>
      ) : null}

      <span className="sr-only" data-testid="relances-voice-tooltip-anchor">
        {tVoice('relances.subtitle')}
      </span>
    </div>
  );
}
